import React, { useEffect, useMemo, useRef, useState } from 'react';
import { v4 } from 'uuid';
import { Dropdown, DropdownList } from '.';
import { PromiseOr } from '../../../util/types.util';
import { toFilterString } from '../../../util/string.util';
import Icon, { IconType } from '../../icons/Icon';
import { Label } from '../Label';

interface DropdownMultiSelectProps<T> {
  items: T[];
  values: Set<T>;
  onSelect: (item: T) => PromiseOr<any>;
  textBuilder: (item: T) => string;
  dropdownItemBuilder?: (item: T) => React.ReactNode;
  inputItemBuilder: (item: T, text: string) => React.ReactNode;
  lastBuilder?: (input: string, selected: boolean) => React.ReactNode;

  queryReset?: string;

  label: string;
  placeholder?: string;
}

export default function DropdownMultiSelect<T>({
  items,
  values,
  queryReset,
  onSelect,
  textBuilder,
  dropdownItemBuilder,
  inputItemBuilder,
  lastBuilder,
  label,
  placeholder,
}: DropdownMultiSelectProps<T>) {
  const inputRef = useRef<HTMLInputElement>(null);

  const [id] = useState(v4());
  const [input, setInput] = useState<string>('');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (queryReset) setInput('');
  }, [queryReset]);

  const handleSelect = (item: T) => {
    onSelect(item);
    if (inputRef.current) inputRef.current.focus();
  };

  const shownItems = useMemo(() => {
    const filterInput = toFilterString(input);
    return items.filter(v => toFilterString(textBuilder(v)).includes(filterInput));
  }, [input, items]);

  return (
    <Dropdown
      open={focused}
      setOpen={open => setFocused(open)}
      dropdown={
        <DropdownList
          items={shownItems}
          textBuilder={textBuilder}
          itemBuilder={dropdownItemBuilder}
          onClick={handleSelect}
          lastBuilder={selected => lastBuilder && lastBuilder(input, selected)}
        />
      }
    >
      <Label label={label} inputId={id}>
        <div
          className='focusable flex min-h-[40px] items-center justify-between gap-2 rounded-2 border border-gray-default stroke-gray-900 px-3 py-2'
          onMouseDown={event => event.preventDefault()}
          onClick={event => {
            if (inputRef.current && !focused) inputRef.current.focus();
          }}
        >
          <div className='flex flex-grow flex-wrap gap-2 '>
            {[...values].map(item => inputItemBuilder(item, textBuilder(item)))}
            <div className='relative flex flex-grow items-center'>
              <input
                ref={inputRef}
                id={id}
                onFocus={() => setFocused(true)}
                onChange={event => setInput(event.currentTarget.value)}
                type={'text'}
                value={input}
                placeholder={placeholder}
                className='unset w-full flex-grow text-sm outline-none placeholder:text-gray-500'
              />
            </div>
          </div>
          <div>
            <Icon type={IconType.chevronDown} size={'size-5'} />
          </div>
        </div>
      </Label>
    </Dropdown>
  );
}
